import React, { useEffect, useRef, useState } from 'react';
import { Agent, api, PublicUser, VaultNode } from './api';
import { Icon } from './icons';
import { EmojiPicker } from './emoji';

export type SlashCommand = { cmd: string; desc: string };
const SLASH_COMMANDS: SlashCommand[] = [
  { cmd: '/catchup', desc: 'AI summary of what you missed' },
  { cmd: '/tone', desc: 'Check tone of your draft' },
  { cmd: '/poll', desc: 'Create a poll' },
  { cmd: '/checklist', desc: 'Create a shared checklist' },
  { cmd: '/kudos', desc: 'Send kudos to a teammate' },
  { cmd: '/call', desc: 'Start a call' },
  { cmd: '/canvas', desc: 'Open the channel canvas' },
  { cmd: '/invite', desc: 'Add people to this channel' },
  { cmd: '/away', desc: 'Set yourself as away' },
  { cmd: '/active', desc: 'Set yourself as active' },
  { cmd: '/status', desc: 'Set a status — /status 🎧 Focusing' },
  { cmd: '/shrug', desc: 'Appends ¯\\_(ツ)_/¯' },
];

export function Composer({
  value,
  placeholder,
  spellcheck,
  enterSends,
  codeFenceEnter,
  uploading,
  recording,
  clipRecording,
  hasAttachments,
  agentWorking = false,
  members,
  agents,
  callsEnabled,
  editing,
  upArrowMode,
  onChange,
  onSend,
  onPickFiles,
  onAddFiles,
  onVideo,
  onToggleRecord,
  onToggleClip,
  onCommand,
  onSchedule,
  onViewScheduled,
  onEditLast,
  onFocusList,
  onCancelEdit,
  attachments,
  workspaceId,
  onMentionOpen,
}: {
  value: string;
  placeholder: string;
  spellcheck: boolean;
  enterSends: boolean;
  codeFenceEnter: boolean;
  uploading: boolean;
  recording: boolean;
  clipRecording: boolean;
  hasAttachments: boolean;
  agentWorking?: boolean; // an agent in this conversation is processing — lock input
  members: PublicUser[];
  agents: Agent[];
  callsEnabled: boolean;
  editing: boolean;
  upArrowMode: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onPickFiles: () => void;
  /** Files pasted or dropped onto the composer — uploaded straight to pending. */
  onAddFiles?: (files: File[]) => void;
  onVideo: () => void;
  onToggleRecord: () => void;
  onToggleClip: () => void;
  onCommand: (cmd: string, arg: string) => void;
  onSchedule: (iso: string) => void;
  onViewScheduled: () => void;
  onEditLast: () => void;
  onFocusList: () => void;
  onCancelEdit: () => void;
  attachments?: React.ReactNode;
  workspaceId?: string;
  /** Called when the @-mention popover opens — refresh the member list so
   *  people who joined after page load are mentionable. */
  onMentionOpen?: () => void;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [fmtOpen, setFmtOpen] = useState(true);
  const [pop, setPop] = useState<null | 'emoji' | 'mention' | 'slash' | 'schedule' | 'wikilink'>(null);
  const [mentionQ, setMentionQ] = useState('');
  const [mentionIdx, setMentionIdx] = useState(0); // highlighted row in the @-popover
  const [wikiQ, setWikiQ] = useState('');
  const [wikiResults, setWikiResults] = useState<VaultNode[]>([]);

  useEffect(() => {
    const ta = taRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 280)}px`;
    }
  }, [value]);

  const hasOpenFence = ((value.match(/```/g) || []).length % 2) === 1;

  const setAll = (v: string, selStart?: number, selEnd?: number) => {
    onChange(v);
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (ta && selStart !== undefined) {
        ta.focus();
        ta.setSelectionRange(selStart, selEnd ?? selStart);
      }
    });
  };

  const wrap = (prefix: string, suffix = prefix, placeholderText = 'text') => {
    const ta = taRef.current;
    if (!ta) return;
    const s = ta.selectionStart ?? value.length;
    const e = ta.selectionEnd ?? value.length;
    const sel = value.slice(s, e) || placeholderText;
    const next = value.slice(0, s) + prefix + sel + suffix + value.slice(e);
    setAll(next, s + prefix.length, s + prefix.length + sel.length);
  };

  const linePrefix = (prefix: string | ((i: number) => string)) => {
    const ta = taRef.current;
    if (!ta) return;
    const s = ta.selectionStart ?? 0;
    const e = ta.selectionEnd ?? 0;
    const before = value.lastIndexOf('\n', Math.max(0, s - 1)) + 1;
    const endIdx = value.indexOf('\n', e);
    const end = endIdx === -1 ? value.length : endIdx;
    const chunk = value.slice(before, end);
    const lines = chunk.split('\n');
    const prefixed = lines
      .map((l, i) => (typeof prefix === 'function' ? prefix(i) : prefix) + l)
      .join('\n');
    setAll(value.slice(0, before) + prefixed + value.slice(end), before, before + prefixed.length);
  };

  const insertAtCursor = (text: string) => {
    const ta = taRef.current;
    const s = ta?.selectionStart ?? value.length;
    const e = ta?.selectionEnd ?? value.length;
    setAll(value.slice(0, s) + text + value.slice(e), s + text.length);
  };

  const addLink = () => {
    const url = window.prompt('Link URL', 'https://');
    if (!url) return;
    const ta = taRef.current;
    const s = ta?.selectionStart ?? value.length;
    const e = ta?.selectionEnd ?? value.length;
    const sel = value.slice(s, e);
    insertAtCursor(sel ? '' : url);
    if (sel) setAll(`${value.slice(0, s)}${sel} (${url})${value.slice(e)}`, e + url.length + 3);
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Escape closes an open popover (mention/emoji/slash/…) before anything else —
    // otherwise the only way out of an accidental "@" was deleting the character.
    if (e.key === 'Escape' && pop) {
      e.preventDefault();
      e.stopPropagation();
      setPop(null);
      return;
    }
    if (e.key === 'Escape' && editing) {
      e.preventDefault();
      e.stopPropagation();
      onCancelEdit();
      return;
    }
    // ── @-mention popover keyboard nav — arrow keys move the highlight, Enter
    // or Tab picks it, so you never have to reach for the mouse mid-type. ──
    if (pop === 'mention' && mentionShown.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIdx((i) => (i + 1) % mentionShown.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIdx((i) => (i - 1 + mentionShown.length) % mentionShown.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickMention(mentionShown[Math.min(mentionIdx, mentionShown.length - 1)].name);
        return;
      }
    }
    if (e.key === 'ArrowUp') {
      const ta = taRef.current;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        onEditLast();
        return;
      }
      if (upArrowMode === 'edit_last' && !value) {
        e.preventDefault();
        onEditLast();
        return;
      }
      if (
        (upArrowMode === 'focus_list' && (ta?.selectionStart ?? 0) === 0) ||
        (upArrowMode === 'focus_list_when_empty' && !value)
      ) {
        e.preventDefault();
        onFocusList();
        return;
      }
    }
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (pop === 'mention' || pop === 'slash' || pop === 'wikilink') return;
    const inFence = codeFenceEnter && hasOpenFence;
    if (enterSends && !inFence) {
      e.preventDefault();
      onSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    // Autosize synchronously on the element itself — the value-effect alone
    // proved unreliable (composer stuck at one line on multi-line drafts).
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 280)}px`;
    // Auto-update guard: a background self-reload would lose an unsent draft.
    (window as any).__fxDraftPending = v.trim().length > 0;
    onChange(v);
    // typing "@..." at the cursor opens the mention popover; "/" at start opens slash
    const caret = e.target.selectionStart ?? v.length;
    const upto = v.slice(0, caret);
    const wiki = upto.match(/\[\[([^\]]{0,60})$/);
    // Only treat "@" as a mention when it starts a word (preceded by whitespace or
    // line-start) — otherwise typing an email like name@domain pops the mention list.
    const at = upto.match(/(?:^|\s)@([\w.-]*)$/);
    if (wiki && workspaceId) {
      const q = wiki[1].toLowerCase();
      setWikiQ(q);
      setPop('wikilink');
      if (q.length >= 1) {
        api.vaultNodes(workspaceId, undefined, q).then(setWikiResults).catch(() => {});
      } else {
        api.vaultNodes(workspaceId).then(setWikiResults).catch(() => {});
      }
    } else if (at) {
      setMentionQ(at[1].toLowerCase());
      if (pop !== 'mention') onMentionOpen?.();
      setPop('mention');
    } else if (v === '/') {
      setPop('slash');
    } else if (pop === 'mention' || pop === 'slash' || pop === 'wikilink') {
      setPop(null);
    }
  };

  const pickMention = (name: string) => {
    const ta = taRef.current;
    const caret = ta?.selectionStart ?? value.length;
    const upto = value.slice(0, caret).replace(/@([\w.-]*)$/, `@${name} `);
    setAll(upto + value.slice(caret), upto.length);
    setPop(null);
  };

  const pickWikiLink = (node: VaultNode) => {
    const ta = taRef.current;
    const caret = ta?.selectionStart ?? value.length;
    const upto = value.slice(0, caret).replace(/\[\[([^\]]{0,60})$/, `[[${node.title}]] `);
    setAll(upto + value.slice(caret), upto.length);
    setPop(null);
  };

  const runSlash = (cmd: string) => {
    setPop(null);
    if (cmd === '/shrug') {
      onChange((value === '/' ? '' : value) + '¯\\_(ツ)_/¯');
      return;
    }
    const arg = value.startsWith(cmd) ? value.slice(cmd.length).trim() : '';
    onCommand(cmd, arg);
    if (value === '/' || value.startsWith(cmd)) onChange('');
  };

  const scheduleOptions = (): { label: string; iso: string }[] => {
    const inHour = new Date(Date.now() + 3600_000);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    const monday = new Date();
    monday.setDate(monday.getDate() + ((8 - monday.getDay()) % 7 || 7));
    monday.setHours(9, 0, 0, 0);
    return [
      { label: `In 1 hour (${inHour.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`, iso: inHour.toISOString() },
      { label: 'Tomorrow at 9:00', iso: tomorrow.toISOString() },
      { label: 'Monday at 9:00', iso: monday.toISOString() },
    ];
  };

  const mentionables = [
    // Group mention first — @team notifies every member of this channel.
    { name: 'team', kind: 'group' },
    ...members.map((m) => ({ name: m.name || m.email || '', kind: 'member' })),
    ...agents.map((a) => ({ name: a.name, kind: 'agent' })),
  ].filter((m) => m.name && m.name.toLowerCase().includes(mentionQ));
  // The rows actually shown (and arrow-key navigable) — capped for height.
  const mentionShown = mentionables.slice(0, 8);
  // Keep the highlight in range as the query narrows the list.
  useEffect(() => { setMentionIdx(0); }, [mentionQ, pop]);

  // Click/tap anywhere outside the composer dismisses an open popover.
  useEffect(() => {
    if (!pop) return undefined;
    const onDocDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setPop(null);
    };
    document.addEventListener('pointerdown', onDocDown);
    return () => document.removeEventListener('pointerdown', onDocDown);
  }, [pop]);

  const [dragOver, setDragOver] = useState(false);
  // Paste an image (screenshot, copied file) → upload it instead of dumping a
  // blob URL into the text. Plain-text paste is left untouched.
  const onPaste = (e: React.ClipboardEvent) => {
    if (!onAddFiles) return;
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) { e.preventDefault(); onAddFiles(files); }
  };
  // Drag-and-drop files/images anywhere onto the composer.
  const onDrop = (e: React.DragEvent) => {
    if (!onAddFiles) return;
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) { e.preventDefault(); onAddFiles(files); }
    setDragOver(false);
  };

  return (
    <div ref={rootRef}
      className={`fx-composer fx-composer--rich${editing ? ' is-editing' : ''}${dragOver ? ' is-drag' : ''}`}
      onPaste={onPaste}
      onDrop={onDrop}
      onDragOver={(e) => { if (onAddFiles && e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
    >
      {editing && (
        <div className="fx-editbar">
          ✏️ Editing message — <span className="fx-kbd">Esc</span> to cancel
          <button type="button" className="fx-editbar-x" onClick={onCancelEdit}>Cancel</button>
        </div>
      )}
      {fmtOpen && (
        <div className="fx-fmtbar">
          <button type="button" className="fx-fmt" title="Bold" onClick={() => wrap('**')}><b>B</b></button>
          <button type="button" className="fx-fmt" title="Italic" onClick={() => wrap('_')}><i>I</i></button>
          <button type="button" className="fx-fmt" title="Underline" onClick={() => wrap('__')}><u>U</u></button>
          <button type="button" className="fx-fmt" title="Strikethrough" onClick={() => wrap('~')}><s>S</s></button>
          <span className="fx-fmt-sep" />
          <button type="button" className="fx-fmt" title="Link" onClick={addLink}><Icon name="link" size={15} /></button>
          <span className="fx-fmt-sep" />
          <button type="button" className="fx-fmt" title="Numbered list" onClick={() => linePrefix((i) => `${i + 1}. `)}><Icon name="listOl" size={15} /></button>
          <button type="button" className="fx-fmt" title="Bulleted list" onClick={() => linePrefix('- ')}><Icon name="listUl" size={15} /></button>
          <span className="fx-fmt-sep" />
          <button type="button" className="fx-fmt" title="Blockquote" onClick={() => linePrefix('> ')}><Icon name="quote" size={15} /></button>
          <button type="button" className="fx-fmt" title="Code" onClick={() => wrap('`')}><Icon name="code" size={15} /></button>
          <button type="button" className="fx-fmt" title="Code block" onClick={() => wrap('```\n', '\n```', 'code')}><Icon name="codeblock" size={15} /></button>
        </div>
      )}

      {attachments}

      <textarea
        ref={taRef}
        className="fx-input fx-input-ta"
        rows={1}
        placeholder={agentWorking ? 'The agent is working — hold on…' : placeholder}
        value={value}
        disabled={agentWorking}
        spellCheck={spellcheck}
        // enterKeyHint is real DOM (mobile keyboards show "Send") but missing
        // from this @types/react version's textarea attributes.
        {...({ enterKeyHint: 'send' } as object)}
        autoCapitalize="sentences"
        onChange={handleChange}
        onKeyDown={onKey}
      />

      <div className="fx-actionbar">
        <button type="button" className="fx-act" title="Attach a file" onClick={onPickFiles} disabled={uploading}>
          {uploading ? '…' : <Icon name="plus" size={16} />}
        </button>
        <button type="button" className={`fx-act${fmtOpen ? ' is-on' : ''}`} title="Formatting" onClick={() => setFmtOpen((v) => !v)}>
          <span className="fx-aa">Aa</span>
        </button>
        <div className="fx-popwrap">
          <button type="button" className="fx-act" title="Emoji" onClick={() => setPop(pop === 'emoji' ? null : 'emoji')}>
            <Icon name="smiley" size={16} />
          </button>
          {pop === 'emoji' && (
            <div className="fx-pop fx-pop-emoji">
              <EmojiPicker onPick={(e) => { insertAtCursor(e); setPop(null); }} />
            </div>
          )}
        </div>
        <div className="fx-popwrap">
          <button
            type="button"
            className="fx-act"
            title="Mention someone"
            onClick={() => { insertAtCursor('@'); setMentionQ(''); onMentionOpen?.(); setPop('mention'); }}
          >
            <Icon name="at" size={16} />
          </button>
          {pop === 'mention' && (
            <div className="fx-pop fx-pop-list">
              {mentionShown.length === 0 && <div className="fx-pop-empty">No matches</div>}
              {mentionShown.map((m, idx) => (
                <button type="button" key={`${m.kind}-${m.name}`}
                  className={`fx-pop-item${idx === mentionIdx ? ' is-active' : ''}`}
                  onMouseEnter={() => setMentionIdx(idx)}
                  onClick={() => pickMention(m.name)}>
                  <span className="fx-pre">{m.kind === 'agent' ? '🤖' : m.kind === 'group' ? '📣' : '@'}</span> {m.name}
                  {m.kind === 'group' && <span className="fx-pop-note"> notifies everyone here</span>}
                </button>
              ))}
            </div>
          )}
          {pop === 'wikilink' && (
            <div className="fx-pop fx-pop-list fx-pop-wiki">
              {wikiResults.length === 0 && <div className="fx-pop-empty">No docs found</div>}
              {wikiResults.slice(0, 8).map((n) => (
                <button type="button" key={n.id} className="fx-pop-item" onClick={() => pickWikiLink(n)}>
                  <span className="fx-pre">{n.kind === 'doc' ? '📄' : n.kind === 'file' ? '📎' : n.kind === 'kb_entry' ? '📚' : '📌'}</span>
                  {n.title}
                  <span className="fx-pop-desc">{n.kind}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="fx-fmt-sep" />
        <button type="button" className="fx-act" title={callsEnabled ? 'Start a video call' : 'Calls need LiveKit configured'} onClick={onVideo}>
          <Icon name="video" size={16} />
        </button>
        <button
          type="button"
          className={`fx-act${recording ? ' is-rec' : ''}`}
          title={recording ? 'Stop recording' : 'Record a voice note'}
          onClick={onToggleRecord}
        >
          <Icon name="mic" size={16} />
          {recording && <span className="fx-rec-dot" />}
        </button>
        <button
          type="button"
          className={`fx-act${clipRecording ? ' is-rec' : ''}`}
          title={clipRecording ? 'Stop the clip' : 'Record a screen + camera clip'}
          onClick={onToggleClip}
        >
          <Icon name="screen" size={16} />
          {clipRecording && <span className="fx-rec-dot" />}
        </button>
        <div className="fx-popwrap">
          <button type="button" className="fx-act" title="Shortcuts" onClick={() => setPop(pop === 'slash' ? null : 'slash')}>
            <Icon name="slashSquare" size={16} />
          </button>
          {pop === 'slash' && (
            <div className="fx-pop fx-pop-list">
              {SLASH_COMMANDS.map((c) => (
                <button type="button" key={c.cmd} className="fx-pop-item" onClick={() => runSlash(c.cmd)}>
                  <b>{c.cmd}</b>
                  <span className="fx-pop-desc">{c.desc}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="fx-actionbar-spacer" />

        <div className="fx-sendgroup">
          <button type="button" className="fx-sendbtn fx-send-ic" onClick={onSend} disabled={agentWorking || (!value.trim() && !hasAttachments)}>
            <Icon name="plane" size={15} />
          </button>
          <div className="fx-popwrap">
            <button type="button" className="fx-sendbtn fx-send-chev" title="Schedule send" onClick={() => setPop(pop === 'schedule' ? null : 'schedule')}>
              <Icon name="chevronDown" size={13} />
            </button>
            {pop === 'schedule' && (
              <div className="fx-pop fx-pop-list fx-pop-up">
                <div className="fx-pop-title">Schedule message</div>
                {scheduleOptions().map((o) => (
                  <button type="button" key={o.label} className="fx-pop-item" onClick={() => { setPop(null); onSchedule(o.iso); }}>
                    {o.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="fx-pop-item"
                  onClick={() => {
                    const v = window.prompt('Send at (YYYY-MM-DD HH:MM, local time)');
                    if (!v) return;
                    const d = new Date(v.replace(' ', 'T'));
                    if (Number.isNaN(d.getTime()) || d.getTime() <= Date.now()) {
                      window.alert('That time is invalid or in the past.');
                      return;
                    }
                    setPop(null);
                    onSchedule(d.toISOString());
                  }}
                >
                  Custom time…
                </button>
                <button type="button" className="fx-pop-item" onClick={() => { setPop(null); onViewScheduled(); }}>
                  View scheduled ↗
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
